import { config } from "@/app/config";
import { useListOrganizationsQuery } from "@/features/organizations/api/organizationsApi";
import { CreateOrganizationModal } from "@/features/organizations/components/CreateOrganizationModal";
import { EditOrganizationModal } from "@/features/organizations/components/EditOrganizationModal";
import { ErrorState } from "@/shared/components/ErrorState";
import type { OrganizationResponse } from "@keystone/contracts/organizations";
import {
  ActionIcon,
  Anchor,
  Avatar,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { DataTable, type DataTableSortStatus } from "mantine-datatable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiPlus, FiRefreshCw, FiSearch } from "react-icons/fi";

type SortAccessor = "name" | "slug" | "customerDomain";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

function compareStrings(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

export function OrganizationsPage() {
  const { t } = useTranslation("organizations");
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editTarget, setEditTarget] = useState<OrganizationResponse | null>(null);
  const { data, isLoading, isFetching, error, refetch } = useListOrganizationsQuery();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<OrganizationResponse>>({
    columnAccessor: "name",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((org) => {
      return (
        org.name.toLowerCase().includes(q) ||
        org.slug.toLowerCase().includes(q) ||
        (org.customerDomain?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [items, debouncedSearch]);

  const sorted = useMemo(() => {
    const accessor = sortStatus.columnAccessor as SortAccessor;
    const dir = sortStatus.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor === "customerDomain" ? a.customerDomain : a[accessor];
      const bv = accessor === "customerDomain" ? b.customerDomain : b[accessor];
      return compareStrings(av, bv) * dir;
    });
  }, [filtered, sortStatus]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paged = sorted.slice(start, start + pageSize);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Title order={2}>{t("page.title")}</Title>
        <Group gap="sm" wrap="nowrap" w={{ base: "100%", sm: "auto" }}>
          <TextInput
            leftSection={<FiSearch aria-hidden="true" />}
            placeholder={t("filter.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            aria-label={t("filter.placeholder")}
            flex={{ base: 1, sm: "0 0 auto" }}
          />
          <Tooltip label={t("actions.reload")}>
            <ActionIcon
              variant="default"
              size="lg"
              loading={isFetching}
              onClick={() => void refetch()}
              aria-label={t("actions.reload")}
            >
              <FiRefreshCw aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<FiPlus aria-hidden="true" />} onClick={openModal} visibleFrom="sm">
            {t("actions.create")}
          </Button>
          <Tooltip label={t("actions.create")} hiddenFrom="sm">
            <ActionIcon
              variant="filled"
              size="lg"
              onClick={openModal}
              aria-label={t("actions.create")}
              hiddenFrom="sm"
            >
              <FiPlus aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {error ? (
        <ErrorState message={t("errors.listFailed")} onRetry={() => void refetch()} />
      ) : (
        <Card withBorder padding={0}>
          <DataTable<OrganizationResponse>
            minHeight={300}
            withTableBorder={false}
            borderRadius="sm"
            highlightOnHover
            striped
            fetching={isLoading || isFetching}
            noRecordsText={
              debouncedSearch.trim() ? t("empty.filtered.description") : t("empty.description")
            }
            records={paged}
            idAccessor="id"
            totalRecords={sorted.length}
            recordsPerPage={pageSize}
            page={safePage}
            onPageChange={setPage}
            recordsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            columns={[
              {
                accessor: "name",
                title: t("table.name"),
                sortable: true,
                render: (org) => (
                  <Group gap="sm" wrap="nowrap">
                    <Avatar src={org.logoUrl} alt="" radius="sm" size="sm" />
                    <Text>{org.name}</Text>
                  </Group>
                ),
              },
              {
                accessor: "slug",
                title: t("table.slug"),
                sortable: true,
                render: (org) => (
                  <Text c="dimmed" ff="monospace" size="sm">
                    {org.slug}
                  </Text>
                ),
              },
              {
                accessor: "customerDomain",
                title: t("table.customerDomain"),
                sortable: true,
                render: (org) =>
                  org.customerDomain ? (
                    <Anchor
                      href={`https://${org.customerDomain}`}
                      target="_blank"
                      rel="noreferrer"
                      size="sm"
                    >
                      {org.customerDomain}
                    </Anchor>
                  ) : (
                    <Text c="dimmed" size="sm">
                      —
                    </Text>
                  ),
              },
              {
                accessor: "id",
                title: t("table.auth0Id"),
                render: (org) => (
                  <Anchor
                    href={`https://manage.auth0.com/dashboard/ca/${config.auth0TenantId}/organizations/${org.id}/overview`}
                    target="_blank"
                    rel="noreferrer"
                    ff="monospace"
                    size="sm"
                  >
                    {org.id}
                  </Anchor>
                ),
              },
              {
                accessor: "actions",
                title: "",
                width: "0%",
                render: (org) => (
                  <Tooltip label={t("actions.edit")}>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => setEditTarget(org)}
                      aria-label={t("actions.edit")}
                    >
                      <FiEdit2 aria-hidden="true" />
                    </ActionIcon>
                  </Tooltip>
                ),
              },
            ]}
          />
        </Card>
      )}

      <CreateOrganizationModal opened={modalOpened} onClose={closeModal} />
      {editTarget && (
        <EditOrganizationModal organization={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </Stack>
  );
}
